// fallow-ignore-file code-duplication
import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  MonthlyTrendingComparativeViewValues,
  type MonthlyTrendingComparativeView,
  type ReportLineType,
} from '@erp/shared-types';
import DashboardLayout from 'src/layouts/dashboard/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  MultiSelect,
  MultiSelectOption,
} from '@/components/common/MultiSelect';
import { SingleSelect } from '@/components/common/SingleSelect';
import { ReportTable } from '@/components/reports/ReportTable';
import { useMonthlyTrendingWithPrefetch } from './useMonthlyTrendingWithPrefetch';
import useDivisions from '@/api/organization/useDivisions';
import useRebateAgreementYears from '@/api/rebates/useRebateAgreementYears';
import { TableSkeleton } from '@/components/common/loading-skeletons/table-skeleton';
import { ShareNetworkIcon, LinkIcon } from '@phosphor-icons/react';
import { ShareReportModal } from 'src/components/reports/ShareReportModal';
import { SharedLinksManager } from 'src/components/reports/SharedLinksManager';
import { DownloadModal } from 'src/components/reports/DownloadModal';
import { downloadRebateMultiTableXlsx } from '@/components/rebates/reports/export/downloadRebateReportXlsx';
import { RebateReportDownloadMenu } from '@/components/rebates/reports/export/RebateReportDownloadMenu';
import {
  rebateMultiTableDownloadFilename,
  rebateMultiTableFinalFilename,
} from '@/components/rebates/reports/export/rebateMultiTableDownloadFilename';
import { downloadMultiTableCSV } from '@/utils/csvDownload';
import { sortReportTableByDivision } from '@/utils/reportTableUtils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { T, useGT } from 'gt-react';
import { HeaderPageTitle } from 'src/context/header-title.context';
import {
  comparativeViewFromShareMetadata,
  comparativeViewShareMetadata,
} from './monthlyTrendingShare';
import {
  MonthlyTrendingComparativeViewToggle,
  MonthlyTrendingLineSelects,
} from './MonthlyTrendingLineSelects';

const REPORT_ID = 'monthly-trending';

export interface MonthlyTrendingPageProps {
  /** When provided, these filters are locked and their UI is hidden */
  lockedFilters?: {
    year: number;
    divisionIds?: string[];
    comparativeView?: string;
  };
}

export function MonthlyTrendingPage({
  lockedFilters,
}: MonthlyTrendingPageProps) {
  const gt = useGT();
  const isShared = !!lockedFilters;

  const reportTitle = gt('Monthly Trending Report', {
    context: 'report name, monthly trending rebate report',
  });

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1; // 1-12
  const defaultYear = lockedFilters?.year ?? currentYear;
  const [selectedYear, setSelectedYear] = useState<number>(defaultYear);
  const [selectedDivisions, setSelectedDivisions] = useState<
    MultiSelectOption[]
  >([]);
  const hasSetDefaultDivision = useRef(false);
  const [priorYearLineType, setPriorYearLineType] =
    useState<ReportLineType>('estimates');
  const [comparativeView, setComparativeView] =
    useState<MonthlyTrendingComparativeView>(() =>
      comparativeViewFromShareMetadata(lockedFilters?.comparativeView),
    );
  const [throughMonth, setThroughMonth] = useState<number>(currentMonth);

  const monthNames = useMemo(
    () => [
      gt('January', { context: 'calendar month name' }),
      gt('February', { context: 'calendar month name' }),
      gt('March', { context: 'calendar month name' }),
      gt('April', { context: 'calendar month name' }),
      gt('May', { context: 'calendar month name' }),
      gt('June', { context: 'calendar month name' }),
      gt('July', { context: 'calendar month name' }),
      gt('August', { context: 'calendar month name' }),
      gt('September', { context: 'calendar month name' }),
      gt('October', { context: 'calendar month name' }),
      gt('November', { context: 'calendar month name' }),
      gt('December', { context: 'calendar month name' }),
    ],
    [gt],
  );
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [linksManagerOpen, setLinksManagerOpen] = useState(false);
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [downloadFormat, setDownloadFormat] = useState<'csv' | 'xlsx'>('csv');

  // Fetch divisions for filter (skip if locked)
  const { data: divisionsData, isLoading: divisionsLoading } =
    useDivisions(!isShared);

  // Fetch available years for the year selector
  const { data: availableYearsData } = useRebateAgreementYears();
  const availableYears =
    availableYearsData && 'years' in availableYearsData
      ? availableYearsData.years
      : undefined;

  // Prepare division options
  const divisionOptions: MultiSelectOption[] = useMemo(() => {
    if (!divisionsData?.divisions) return [];
    return divisionsData.divisions.map((division) => ({
      id: division.id,
      label: division.name,
      value: division,
      group: division.tags?.[0]?.name,
    }));
  }, [divisionsData?.divisions]);

  // Prepare year options - descending order, no future years
  const yearOptions = useMemo(() => {
    if (!availableYears || availableYears.length === 0) {
      // Fallback to last 5 years if no years available
      const years: number[] = [];
      for (let i = 0; i < 5; i++) {
        years.push(currentYear - i);
      }
      return years.map((year) => ({
        id: year.toString(),
        label: year.toString(),
        value: year,
      }));
    }
    return availableYears
      .filter((year) => year <= currentYear) // Exclude future years
      .sort((a, b) => b - a) // Descending order
      .map((year) => ({
        id: year.toString(),
        label: year.toString(),
        value: year,
      }));
  }, [availableYears, currentYear]);

  // Prepare prior year line type options for comparative table
  const priorYearLineTypeOptions: Array<{
    id: ReportLineType;
    label: string;
    value: ReportLineType;
  }> = useMemo(
    () => [
      {
        id: 'estimates',
        label: gt('Estimates', {
          context: 'prior year line type option, estimated values',
        }),
        value: 'estimates',
      },
      {
        id: 'reported',
        label: gt('Reported', {
          context: 'prior year line type option, reported values',
        }),
        value: 'reported',
      },
      {
        id: 'predictions',
        label: gt('Predictions', {
          context: 'prior year line type option, predicted values',
        }),
        value: 'predictions',
      },
    ],
    [gt],
  );

  // Determine effective filter values (locked or selected)
  const effectiveYear = lockedFilters?.year ?? selectedYear;
  const effectiveDivisionIds =
    lockedFilters?.divisionIds ??
    (selectedDivisions.length > 0
      ? selectedDivisions.map((d) => d.id)
      : undefined);

  // Generate default filename with divisions
  const defaultFilename = useMemo(() => {
    const divisionPart =
      !isShared &&
      selectedDivisions.length > 0 &&
      selectedDivisions.length < divisionOptions.length
        ? `-${selectedDivisions.map((d) => d.label.replace(/\s+/g, '-')).join('-')}`
        : '';
    const viewPart =
      comparativeView === MonthlyTrendingComparativeViewValues.REPORTED
        ? `-${comparativeView}`
        : '';
    return `monthly-trending-${effectiveYear}${viewPart}${divisionPart}`;
  }, [
    effectiveYear,
    comparativeView,
    selectedDivisions,
    divisionOptions.length,
    isShared,
  ]);

  // Handler for downloading all tables
  const handleDownloadClick = (format: 'csv' | 'xlsx') => {
    if (!report) return;
    setDownloadFormat(format);
    setDownloadModalOpen(true);
  };

  const handleDownload = (filename: string) => {
    if (!sortedTable || !sortedBreakdownTables) return;

    const divisionHeader = gt('Division', {
      context: 'CSV row label column header, division name',
    });

    const options = {
      tables: [
        {
          title:
            comparativeView === MonthlyTrendingComparativeViewValues.REPORTED
              ? `${sortedTable.title} - ${gt('Reported', {
                  context:
                    'CSV export section title suffix, reported comparative YTD view',
                })}`
              : sortedTable.title ||
                gt('Category Breakdown', {
                  context: 'CSV export section title, category breakdown table',
                }),
          table: sortedTable,
          rowLabelHeader: divisionHeader,
        },
        {
          title: sortedBreakdownTables.total.title,
          table: sortedBreakdownTables.total,
          rowLabelHeader: divisionHeader,
        },
        {
          title: sortedBreakdownTables.redistribution.title,
          table: sortedBreakdownTables.redistribution,
          rowLabelHeader: divisionHeader,
        },
        {
          title: sortedBreakdownTables.conditional.title,
          table: sortedBreakdownTables.conditional,
          rowLabelHeader: divisionHeader,
        },
        {
          title: sortedBreakdownTables.nonConditional.title,
          table: sortedBreakdownTables.nonConditional,
          rowLabelHeader: divisionHeader,
        },
      ],
      filename: rebateMultiTableFinalFilename(filename, downloadFormat),
    };
    if (downloadFormat === 'xlsx') {
      void downloadRebateMultiTableXlsx(options);
      return;
    }
    downloadMultiTableCSV(options);
  };

  // Default to ALL divisions when divisions are loaded (only once, skip if locked)
  useEffect(() => {
    if (isShared) return; // Skip if using locked filters
    if (
      divisionOptions.length > 0 &&
      selectedDivisions.length === 0 &&
      !divisionsLoading &&
      !hasSetDefaultDivision.current
    ) {
      setSelectedDivisions([...divisionOptions]);
      hasSetDefaultDivision.current = true;
    }
  }, [divisionOptions, selectedDivisions.length, divisionsLoading, isShared]);

  const reportQueryEnabled =
    isShared ||
    (!divisionsLoading &&
      (selectedDivisions.length > 0 || divisionOptions.length === 0));

  // Fetch report data
  const {
    data: report,
    isLoading,
    error,
  } = useMonthlyTrendingWithPrefetch({
    year: effectiveYear,
    divisionIds: effectiveDivisionIds,
    priorYearLineType,
    throughMonth,
    comparativeView,
    enabled: reportQueryEnabled,
  });
  const showReportSkeleton = isLoading || !reportQueryEnabled;

  // Sort report tables by custom division order
  const sortedTable = useMemo(() => {
    if (!report?.table) return null;
    return sortReportTableByDivision(report.table);
  }, [report?.table]);

  const sortedBreakdownTables = useMemo(() => {
    const tables = report?.monthlyBreakdownTables;
    if (!tables) return null;
    return {
      total: sortReportTableByDivision(tables.total),
      redistribution: sortReportTableByDivision(tables.redistribution),
      conditional: sortReportTableByDivision(tables.conditional),
      nonConditional: sortReportTableByDivision(tables.nonConditional),
    };
  }, [report?.monthlyBreakdownTables]);

  const breakdownTableKeys = [
    'total',
    'redistribution',
    'conditional',
    'nonConditional',
  ] as const;

  const content = (
    <div
      className={
        isShared ? '' : 'p-1 w-full min-w-0 max-w-full overflow-hidden'
      }
    >
      {isShared ? (
        <h1 className="mb-2 text-2xl font-bold">{reportTitle}</h1>
      ) : (
        <HeaderPageTitle>{reportTitle}</HeaderPageTitle>
      )}

      {/* Filters (flush, no card) */}
      <div className="mb-2 space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          {!isShared && (
            <>
              <SingleSelect
                label={gt('Year', {
                  context: 'filter label, calendar year',
                })}
                labelPosition="left"
                options={yearOptions}
                value={
                  yearOptions.find((opt) => opt.value === selectedYear) || null
                }
                onChange={(option) => {
                  if (option) {
                    setSelectedYear(option.value);
                  }
                }}
                placeholder={gt('Select year...', {
                  context: 'year select placeholder',
                })}
                searchPlaceholder={gt('Search years...', {
                  context: 'search years in select',
                })}
                shouldAutoSort={false}
              />
              <MultiSelect
                label={gt('Divisions', {
                  context: 'filter label, business divisions',
                })}
                labelPosition="left"
                options={divisionOptions}
                value={selectedDivisions}
                onChange={setSelectedDivisions}
                placeholder={gt('Select divisions...', {
                  context: 'multi-select placeholder',
                })}
                searchPlaceholder={gt('Search divisions...', {
                  context: 'search inside division multi-select',
                })}
                disabled={divisionsLoading}
              />
            </>
          )}
          <MonthlyTrendingLineSelects
            priorYear={
              (
                priorYearLineTypeOptions.find(
                  (opt) => opt.value === priorYearLineType,
                ) || { value: priorYearLineType }
              ).value
            }
            priorYearOptions={priorYearLineTypeOptions}
            onPriorYearChange={setPriorYearLineType}
            throughMonth={throughMonth}
            monthNames={monthNames}
            onThroughMonthChange={setThroughMonth}
          />
          <div className="ml-auto flex gap-2">
            <RebateReportDownloadMenu
              disabled={!report || isLoading}
              onDownload={handleDownloadClick}
            />
            {!isShared && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    <ShareNetworkIcon className="h-4 w-4 mr-2" />
                    <T context="rebates">Share</T>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setShareModalOpen(true)}>
                    <ShareNetworkIcon className="h-4 w-4 mr-2" />
                    <T context="menu item, create shareable report link">
                      Create share link
                    </T>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setLinksManagerOpen(true)}>
                    <LinkIcon className="h-4 w-4 mr-2" />
                    <T context="menu item, manage existing shared links">
                      Manage shared links
                    </T>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </div>

      {/* Loading State */}
      {showReportSkeleton && (
        <Card>
          <CardHeader>
            <CardTitle>{reportTitle}</CardTitle>
          </CardHeader>
          <CardContent>
            <TableSkeleton />
          </CardContent>
        </Card>
      )}

      {/* Error State */}
      {error && !showReportSkeleton && (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-destructive">
              <T context="error message when rebate report fails to load">
                Error loading report data. Please try again.
              </T>
            </p>
          </CardContent>
        </Card>
      )}

      {/* Report Tables */}
      {sortedTable && sortedBreakdownTables && !showReportSkeleton && (
        <div className="space-y-3">
          {/* First Table: Category Breakdown */}
          <ReportTable
            table={sortedTable}
            timeFilter="all"
            onTimeFilterChange={() => {
              // No-op: time filter is hardcoded to "all" (full year)
            }}
            rowLabel={gt('Division', {
              context: 'report table row label column, division',
            })}
            showTotals={false}
            showTimeFilter={false}
            headerActions={
              <MonthlyTrendingComparativeViewToggle
                value={comparativeView}
                onChange={setComparativeView}
              />
            }
          />

          {/* One monthly breakdown table per rule category, plus a combined total */}
          {breakdownTableKeys.map((key) => (
            <ReportTable
              key={key}
              table={sortedBreakdownTables[key]}
              timeFilter="all"
              onTimeFilterChange={() => {
                // No-op: time filter is hardcoded to "all" (full year)
              }}
              rowLabel={gt('Division', {
                context: 'report table row label column, division',
              })}
              showTotals={true}
              showTimeFilter={false}
            />
          ))}
        </div>
      )}

      {!isShared && (
        <>
          <ShareReportModal
            open={shareModalOpen}
            onClose={() => setShareModalOpen(false)}
            reportId={REPORT_ID}
            reportTitle={reportTitle}
            initialYear={selectedYear}
            initialDivisions={selectedDivisions}
            additionalMetadata={comparativeViewShareMetadata(comparativeView)}
          />
          <SharedLinksManager
            open={linksManagerOpen}
            onClose={() => setLinksManagerOpen(false)}
            reportId={REPORT_ID}
            reportTitle={reportTitle}
            onCreateNew={() => {
              setLinksManagerOpen(false);
              setShareModalOpen(true);
            }}
          />
        </>
      )}
      <DownloadModal
        open={downloadModalOpen}
        onClose={() => setDownloadModalOpen(false)}
        onDownload={handleDownload}
        defaultFilename={rebateMultiTableDownloadFilename(
          defaultFilename,
          downloadFormat,
        )}
        title={
          downloadFormat === 'xlsx'
            ? gt('Download Monthly Trending Report (.xlsx)', {
                context:
                  'modal title for downloading monthly trending report as Excel',
              })
            : gt('Download Monthly Trending Report (.csv)', {
                context:
                  'modal title for downloading monthly trending report as CSV',
              })
        }
        confirmLabel={
          downloadFormat === 'xlsx'
            ? gt('Download Excel', { context: 'rebates' })
            : gt('Download CSV', { context: 'rebates' })
        }
      />
    </div>
  );

  // Wrap in DashboardLayout only when not shared
  if (isShared) {
    return content;
  }

  return <DashboardLayout>{content}</DashboardLayout>;
}

// Default export for routing
const MonthlyTrendingPageDefault: React.FC = () => {
  return <MonthlyTrendingPage />;
};

export default MonthlyTrendingPageDefault;
